import React, {useEffect, useMemo, useRef, useState} from "react";
import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { FileUpload, type FileUploadSelectEvent } from "primereact/fileupload";
import SGrid from "../../components/grid/grid.component";
import type { components } from "../../shared/api/v1/openapi.types";
import { fetch } from "../../shared/api/v1";
import axios from "../../transport/axios";
import { useAuth } from "../../providers/auth/auth-provider";
import { useMessage } from "../../providers/message/message-provider";

type ConnectionUserFilesProps = {
  files: components["schemas"]["File"][];
  onDownload: (file: components["schemas"]["File"]) => void;
  onDeleteSelected: (ids: number[]) => Promise<void>;
  canAdd?: boolean;
  canDelete: boolean;
  showSelectionColumn?: boolean;
  connectionId?: string;
  onConnectionUpdated?: (connection: components["schemas"]["Connection"]) => void;
};

export const ConnectionUserFiles = ({
  files,
  onDownload,
  onDeleteSelected,
  canAdd = false,
  canDelete,
  showSelectionColumn = true,
  connectionId,
  onConnectionUpdated,
}: ConnectionUserFilesProps) => {
  const { token } = useAuth();
  const { showError, showMessage } = useMessage();
  const fileUploadRef = useRef<FileUpload | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<components["schemas"]["File"][]>([]);
  const [previewUrls, setPreviewUrls] = useState<Record<number, string>>({});
  const [addDialogVisible, setAddDialogVisible] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [fileUploadKey, setFileUploadKey] = useState(0);
  const selectionEnabled = canDelete && showSelectionColumn;

  const missingPreviewFiles = useMemo(() => {
    return files.filter((file) => isImageFile(file.name) && !previewUrls[file.id]);
  }, [files, previewUrls]);

  useEffect(() => {
    let isMounted = true;
    const loadPreviews = async () => {
      if (!token || missingPreviewFiles.length === 0) {
        return;
      }
      const entries = await Promise.all(
        missingPreviewFiles.map(async (file) => {
          try {
            const response = await axios.get(file.url, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
              responseType: "blob",
            });
            const blobUrl = window.URL.createObjectURL(response.data);
            return [file.id, blobUrl] as const;
          } catch {
            return [file.id, ""] as const;
          }
        })
      );
      if (!isMounted) {
        return;
      }
      setPreviewUrls((prev) => {
        const nextMap: Record<number, string> = {};
        entries.forEach(([id, url]) => {
          if (url && !prev[id]) {
            nextMap[id] = url;
          }
        });
        return Object.keys(nextMap).length ? { ...prev, ...nextMap } : prev;
      });
    };
    loadPreviews();
    return () => {
      isMounted = false;
    };
  }, [missingPreviewFiles, token]);

  useEffect(() => {
    return () => {
      Object.values(previewUrls).forEach((url) => {
        window.URL.revokeObjectURL(url);
      });
    };
  }, [previewUrls]);

  const previewBody = (row: components["schemas"]["File"]) => {
    if (isImageFile(row.name)) {
      const src = previewUrls[row.id];
      return src ? (
        <img
          src={src}
          alt={row.name}
          style={{ width: "36px", height: "36px", objectFit: "cover", borderRadius: "4px" }}
        />
      ) : (
        <span className="pi pi-image" />
      );
    }
    const icon = getFileIcon(row.name);
    return <span className={`pi ${icon}`} />;
  };

  const handleDeleteSelected = () => {
    if (!canDelete || selectedFiles.length === 0) {
      return;
    }
    confirmDialog({
      message: `Удалить выбранные файлы (${selectedFiles.length})?`,
      header: "Подтверждение",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Удалить",
      rejectLabel: "Отмена",
      accept: async () => {
        const ids = selectedFiles.map((file) => file.id);
        await onDeleteSelected(ids);
        setSelectedFiles([]);
      },
    });
  };

  const extractErrorMessage = (error: unknown) => {
    if (!error || typeof error !== "object") {
      return undefined;
    }
    const maybeMessage = (error as { message?: string }).message;
    if (maybeMessage) {
      return maybeMessage;
    }
    const nestedMessage = (error as { error?: { message?: string } }).error?.message;
    if (nestedMessage) {
      return nestedMessage;
    }
    return (error as { data?: { message?: string } }).data?.message;
  };

  const extractUploadedFileIds = (body: unknown): number[] => {
    if (Array.isArray((body as { fileIds?: number[] })?.fileIds)) {
      return (body as { fileIds: number[] }).fileIds;
    }
    if (Array.isArray(body)) {
      return body as number[];
    }
    if (Array.isArray((body as { ids?: number[] })?.ids)) {
      return (body as { ids: number[] }).ids;
    }
    if (Array.isArray((body as { data?: number[] })?.data)) {
      return (body as { data: number[] }).data;
    }
    return [];
  };

  const handleUploadFiles = async () => {
    if (!token) {
      showError({ message: "Нет авторизации. Пожалуйста, войдите в аккаунт." });
      return;
    }
    if (!connectionId) {
      showError({ message: "Не удалось определить заявку для загрузки файлов." });
      return;
    }
    if (pendingFiles.length === 0) {
      showError({ message: "Выберите файлы для загрузки." });
      return;
    }
    if (pendingFiles.length > 10) {
      showError({ message: "Нельзя загрузить больше 10 файлов за один раз." });
      return;
    }
    setIsUploading(true);
    try {
      const data = new FormData();
      pendingFiles.forEach((file) => {
        data.append("files", file);
      });
      const uploadResponse = await axios.post("/upload/consumer", data, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      const fileIds = extractUploadedFileIds(uploadResponse.data);
      if (fileIds.length === 0) {
        throw new Error("Не удалось получить идентификаторы загруженных файлов.");
      }
      const response = await fetch.PATCH("/connections/{id}/files", {
        params: {
          path: {
            id: Number(connectionId),
          },
        },
        body: {
          fileIds,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const error = (response as { error?: unknown }).error;
      if (error) {
        const message = extractErrorMessage(error);
        throw new Error(message ?? "Ошибка добавления файлов");
      }
      if (response.data) {
        onConnectionUpdated?.(response.data);
      }
      setPendingFiles([]);
      fileUploadRef.current?.clear();
      setAddDialogVisible(false);
      showMessage({ message: "Файлы добавлены" });
    } catch (error) {
      const message = extractErrorMessage(error);
      showError({ message: message ?? "Ошибка загрузки файлов" });
    } finally {
      setIsUploading(false);
    }
  };

  const resetSelection = () => {
    setPendingFiles([]);
    fileUploadRef.current?.clear();
    setFileUploadKey((prev) => prev + 1);
  };

  const header = (
    <div className="flex justify-content-between align-items-center" style={{ minHeight: "39px" }}>
      {canAdd ? (
        <Button
          type="button"
          icon="pi pi-plus"
          label="Добавить"
          onClick={() => {
            setPendingFiles([]);
            setAddDialogVisible(true);
          }}
        />
      ) : (
        <span />
      )}
      {selectionEnabled && selectedFiles.length > 0 ? (
        <Button
          type="button"
          icon="pi pi-trash"
          label="Удалить выбранные"
          severity="danger"
          onClick={handleDeleteSelected}
        />
      ) : null}
    </div>
  );

  const columns = [
    ...(selectionEnabled ? [{ selectionMode: "multiple" as const, headerStyle: { width: "3rem" } }] : []),
    { field: "preview", header: "", body: previewBody },
    { field: "name", header: "Файл" },
    {
      field: "url",
      body: (row: components["schemas"]["File"]) => (
        <Button
          type="button"
          text
          icon="pi pi-download"
          label="Скачать"
          onClick={() => onDownload(row)}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-column gap-2">
      <ConfirmDialog />
      <Dialog
        header="Добавить файлы"
        visible={addDialogVisible}
        style={{ width: "480px" }}
        onHide={() => {
          if (!addDialogVisible) return;
          setAddDialogVisible(false);
        }}
        footer={
          <div className="flex justify-content-end gap-2">
            <Button
              type="button"
              text
              label="Отмена"
              onClick={() => setAddDialogVisible(false)}
              disabled={isUploading}
            />
            <Button
              type="button"
              label="Загрузить"
              icon="pi pi-upload"
              onClick={handleUploadFiles}
              loading={isUploading}
              disabled={pendingFiles.length === 0}
            />
          </div>
        }
      >
        <div className="flex flex-column gap-3">
          <style>{`
            .connection-file-upload .p-fileupload-buttonbar .p-button:not(.p-fileupload-choose),
            .connection-file-upload .p-fileupload-upload,
            .connection-file-upload .p-fileupload-cancel {
              display: none !important;
            }
          `}</style>
          <FileUpload
            key={fileUploadKey}
            ref={fileUploadRef}
            name="files"
            mode="advanced"
            className="connection-file-upload"
            chooseLabel="Выбрать файлы"
            invalidFileSizeMessageSummary="Слишком большой файл"
            invalidFileSizeMessageDetail="Максимальный размер файла: 3 МБ."
            accept="image/png, image/jpeg, image/jpg, application/pdf"
            maxFileSize={3 * 1024 * 1024}
            customUpload
            multiple
            onSelect={(event: FileUploadSelectEvent) => {
              setPendingFiles(event.files ?? []);
            }}
            onClear={() => setPendingFiles([])}
            disabled={isUploading}
          />
          {pendingFiles.length > 0 ? (
            <div className="text-sm text-color-secondary">
              Выбрано файлов: {pendingFiles.length}
            </div>
          ) : (
            <div className="text-sm text-color-secondary">Файлы не выбраны</div>
          )}
        </div>
      </Dialog>
      <SGrid
        header={header}
        value={files}
        dataKey="id"
        selection={selectionEnabled ? selectedFiles : undefined}
        onSelectionChange={
          selectionEnabled
            ? (e: { value?: components["schemas"]["File"][] }) => setSelectedFiles(e.value ?? [])
            : undefined
        }
        selectionMode={selectionEnabled ? "checkbox" : undefined}
        emptyMessage="Документы не загружены"
        paginator={true}
        rows={10}
        columns={columns}
      />
    </div>
  );
};

const getFileExtension = (name?: string) => {
  if (!name) {
    return "";
  }
  const parts = name.split(".");
  if (parts.length < 2) {
    return "";
  }
  return parts[parts.length - 1].toLowerCase();
};

const isImageFile = (name?: string) => {
  const ext = getFileExtension(name);
  return ["png", "jpg", "jpeg", "gif", "webp"].includes(ext);
};

const getFileIcon = (name?: string) => {
  const ext = getFileExtension(name);
  switch (ext) {
    case "pdf":
      return "pi-file-pdf";
    case "xls":
    case "xlsx":
      return "pi-file-excel";
    case "doc":
    case "docx":
      return "pi-file-word";
    case "ppt":
    case "pptx":
      return "pi-file-ppt";
    case "zip":
    case "rar":
    case "7z":
      return "pi-file-zip";
    case "txt":
      return "pi-file";
    default:
      return "pi-file";
  }
};
